"use client"

import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { MoreHorizontal, Edit, Trash2 } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { RouteAreaRate } from "@/utils/routeAreaRate"
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header"
import axiosInstance from "@/utils/axiosInstance"
import { toast } from "sonner"

export const getColumns = (
  onEdit: (rate: RouteAreaRate) => void,
  onDeleteSuccess: () => void
): ColumnDef<RouteAreaRate>[] => [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      return <div className="w-[80px]">{row.getValue("id")}</div>
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "pickup_area",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Pickup Area" />
    ),
    cell: ({ row }) => {
      const pickupArea = row.original.pickup_area
      return (
        <div className="flex flex-col">
          <span className="font-medium">{pickupArea.name}</span>
          <span className="text-sm text-muted-foreground">
            {pickupArea.type}
          </span>
        </div>
      )
    },
    filterFn: (row, _id, value) => {
      // For faceted filter, value is an array of selected values
      if (Array.isArray(value) && value.length > 0) {
        return value.includes(row.original.pickup_area.name)
      }
      return row.original.pickup_area.name.toLowerCase().includes(value.toLowerCase())
    },
  },
  {
    accessorKey: "dropoff_area",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Dropoff Area" />
    ),
    cell: ({ row }) => {
      const dropoffArea = row.original.dropoff_area
      return (
        <div className="flex flex-col">
          <span className="font-medium">{dropoffArea.name}</span>
          <span className="text-sm text-muted-foreground">
            {dropoffArea.type}
          </span>
        </div>
      )
    },
    filterFn: (row, _id, value) => {
      // For faceted filter, value is an array of selected values
      if (Array.isArray(value) && value.length > 0) {
        return value.includes(row.original.dropoff_area.name)
      }
      return row.original.dropoff_area.name.toLowerCase().includes(value.toLowerCase())
    },
  },
  {
    accessorKey: "price",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Price" />
    ),
    cell: ({ row }) => {
      const price = parseFloat(row.getValue("price"))
      const currency = row.getValue("currency") as string
      
      return (
        <div className="font-medium">
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
          }).format(price)}
        </div>
      )
    },
  },
  {
    accessorKey: "currency",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Currency" />
    ),
    cell: ({ row }) => {
      const currency = row.getValue("currency") as string
      return (
        <Badge variant="secondary" className="font-mono">
          {currency}
        </Badge>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
  },
  {
    accessorKey: "description",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      const description = row.getValue("description") as string
      return (
        <div className="max-w-[300px] truncate">
          {description || <span className="text-muted-foreground">No description</span>}
        </div>
      )
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const rate = row.original

      const handleDelete = async () => {
        try {
          await axiosInstance.delete(`/route-area-rates/${rate.id}`)
          toast("Fixed rate deleted successfully")
          onDeleteSuccess()
        } catch (error) {
          toast("Error deleting fixed rate", {
            description: "Please try again or contact support if the problem persists."
          })
        }
      }

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit(rate)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  },
]